import React, { useMemo } from 'react';
import { useSupportTickets, SupportTicket } from '../../crm-support-ticket/useData';
import { useTheme } from '../../../contexts/ThemeContext';

export interface TicketStatusBreakdownProps {
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const defaultColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#6b7280'];
const size = 220;

const TicketStatusBreakdown: React.FC<TicketStatusBreakdownProps> = ({
  embedded,
}) => {
  const { supportTickets, isLoading } = useSupportTickets();
  const { colors } = useTheme();

  const data = useMemo(() => {
    const arr = Array.isArray(supportTickets) ? supportTickets : [];
    const map = new Map<string, number>();
    arr.forEach((t: SupportTicket) => {
      const status = t.statusDescr?.pts_descr || 'Unknown';
      map.set(status, (map.get(status) || 0) + 1);
    });
    return Array.from(map.entries()).map(([status, count], i) => ({
      status,
      count,
      color: defaultColors[i % defaultColors.length],
    }));
  }, [supportTickets]);

  const cardClass = embedded
    ? 'min-w-0'
    : `${colors.surface} rounded-xl shadow-sm p-5 ${colors.border} border`;

  if (isLoading) {
    return (
      <div className={cardClass}>
        <h2 className={`text-base font-semibold ${colors.text} mb-4`}>
          Ticket Status
        </h2>
        <div className="flex items-center justify-center" style={{ height: `${size}px` }}>
          <div className={`animate-pulse ${colors.textMuted}`}>Loading...</div>
        </div>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className={cardClass}>
        <h2 className={`text-base font-semibold ${colors.text} mb-4`}>
          Ticket Status
        </h2>
        <div className="flex items-center justify-center" style={{ height: `${size}px` }}>
          <p className={colors.textMuted}>No tickets</p>
        </div>
      </div>
    );
  }

  const total = data.reduce((s, d) => s + d.count, 0);
  const radius = size / 2 - 20;
  const centerX = size / 2;
  const centerY = size / 2;
  const innerRadius = radius * 0.5;
  let currentAngle = -90;

  const paths = data.map((item, index) => {
    const angle = (item.count / total) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    const x1 = centerX + radius * Math.cos(startRad);
    const y1 = centerY + radius * Math.sin(startRad);
    const x2 = centerX + radius * Math.cos(endRad);
    const y2 = centerY + radius * Math.sin(endRad);
    const x1i = centerX + innerRadius * Math.cos(startRad);
    const y1i = centerY + innerRadius * Math.sin(startRad);
    const x2i = centerX + innerRadius * Math.cos(endRad);
    const y2i = centerY + innerRadius * Math.sin(endRad);
    const large = angle > 180 ? 1 : 0;
    const d = `M ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2} L ${x2i} ${y2i} A ${innerRadius} ${innerRadius} 0 ${large} 0 ${x1i} ${y1i} Z`;
    return { d, color: item.color, status: item.status, count: item.count };
  });

  return (
    <div className={cardClass}>
      <h2 className={`text-base font-semibold ${colors.text} mb-4`}>
        Ticket Status
      </h2>
      <div className="flex flex-col sm:flex-row gap-4 items-center">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="flex-shrink-0">
          {paths.map((p, i) => (
            <path key={i} d={p.d} fill={p.color} stroke="white" strokeWidth={2} />
          ))}
        </svg>
        <ul className="space-y-2 min-w-0">
          {paths.map((p, i) => (
            <li key={i} className="flex items-center gap-2 text-sm">
              <span
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: p.color }}
              />
              <span className={colors.text}>{p.status}</span>
              <span className={colors.textMuted}>({p.count})</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default TicketStatusBreakdown;
